/**
 * Deal creation core — shared by the athlete's own `deal.create` and the
 * agent's on-behalf `agency.createDealFor`.
 *
 * Extracted from `deal.router.ts` so both paths run identical business
 * logic (payor resolution, compensation-item synthesis, amount
 * reconciliation, deliverable category mapping) instead of drifting.
 *
 * THE KEY DISTINCTION this extraction introduces:
 *
 *   actorId   — who is performing the action. All PERMISSION checks run
 *               against this: org membership, payor access.
 *   athleteId — who OWNS the resulting deal.
 *
 * The original handler conflated the two through a single `userId`, which
 * is exactly why no on-behalf path was possible. When actorId ===
 * athleteId and no `createdByAgentId` is passed, behavior is byte-for-byte
 * what it was before — that's the athlete hot path and the highest
 * regression risk in this epic, so it is deliberately unchanged.
 *
 * @package @app/api
 */

import { compensationItemSchema, deriveActivityCategoryFromLegacyType } from '@app/validators';
import { z } from 'zod';
import { captureServerEvent } from './analytics';

/**
 * Revenue model — how this deal originated. Accepted on the input so the
 * client can send it today, but NOT persisted: the DB column doesn't
 * exist yet. A follow-up adds `dealSource` / `marketplaceFee` /
 * `marketplaceFeeRate` via a proper migration.
 */
export const dealSourceEnum = z.enum(['self_sourced', 'nilboard_marketplace', 'brand_outbound']);

export const dealCreateInput = z.object({
  title: z.string().min(1).max(200),
  description: z.string().optional(),
  brandName: z.string().min(1),
  brandContact: z.string().optional(),
  brandEmail: z.string().email().optional(),
  // US-028: structured payor link. Additive — brandName etc. stay required
  // for now so existing clients keep working. When a payorId is provided
  // we also auto-populate brandName from the payor's legalName so the
  // free-text column stays consistent for downstream reads.
  payorId: z.string().optional(),
  amount: z.number().nonnegative(),
  currency: z.string().default('USD'),
  status: z.enum(['DRAFT', 'ACTIVE', 'COMPLETED', 'EXPIRED', 'CANCELLED']).default('DRAFT'),
  startDate: z.string().date().or(z.string().datetime()).optional(),
  endDate: z.string().date().or(z.string().datetime()).optional(),
  source: z.enum(['dm', 'email', 'agent', 'direct']).optional(),
  dealSource: dealSourceEnum.optional(),
  isExclusive: z.boolean().default(false),
  exclusivityTerms: z.string().optional(),
  notes: z.string().optional(),
  organizationId: z.string().optional(),
  aiExtracted: z.boolean().optional(),
  aiConfidence: z.number().optional(),
  aiRawData: z.string().optional(),
  deliverables: z
    .array(
      z.object({
        title: z.string(),
        type: z.string().optional(),
        platform: z.string().optional(),
        dueDate: z.string().optional(),
      }),
    )
    .optional(),
  // US-029: structured compensation items. If supplied, the legacy
  // `amount` field is overwritten with the computed sum so dashboards
  // stay consistent. If omitted, we synthesise a single CASH item from
  // `amount` so existing callers keep working.
  compensationItems: z.array(compensationItemSchema).optional(),
});

export type DealCreateInput = z.infer<typeof dealCreateInput>;

export interface CreateDealCoreArgs {
  /**
   * Prisma client. The athlete path passes the ENHANCED client (row
   * policies apply and are correct — they're acting on their own data).
   * The agent path MUST pass raw prisma: the enhanced client enforces
   * `Deal`'s `auth() == athlete` rule and would reject a write whose
   * athleteId isn't the caller. Authorization for that path is the
   * agency roster check in the calling handler.
   */
  // biome-ignore lint/suspicious/noExplicitAny: prisma client type
  db: any;
  /** Who is performing the action — all permission checks use this. */
  actorId: string;
  /** Who owns the resulting deal. */
  athleteId: string;
  input: DealCreateInput;
  /** Set when an agent creates on an athlete's behalf (D9 provenance). */
  createdByAgentId?: string | null;
  /** Per-deal commission override, else the roster default. */
  commissionPercent?: number | null;
}

/**
 * Create a deal plus its compensation items and deliverables.
 * Returns the new deal's id.
 */
export async function createDealCore({
  db,
  actorId,
  athleteId,
  input,
  createdByAgentId = null,
  commissionPercent = null,
}: CreateDealCoreArgs): Promise<{ id: string }> {
  const { createId } = await import('@app/db');
  // dealSource is accepted but not persisted — DB column doesn't exist yet.
  const { deliverables, compensationItems, dealSource: _dealSource, ...dealData } = input;

  // Verify organization membership if associating with an org. Checked
  // against the ACTOR: they're the one asserting the association.
  if (dealData.organizationId) {
    const membership = await db.member.findFirst({
      where: { userId: actorId, organizationId: dealData.organizationId },
    });
    if (!membership) {
      throw new Error('You are not a member of this organization');
    }
  }

  // US-028: when a payor is referenced, verify the ACTOR can use it and
  // keep the legacy free-text brandName consistent.
  if (dealData.payorId) {
    const payor = await db.payor.findUnique({ where: { id: dealData.payorId } });
    if (!payor) throw new Error('Payor not found');
    const ownsIt = payor.ownerId === actorId;
    let orgShared = false;
    if (!ownsIt && payor.organizationId) {
      const m = await db.member.findFirst({
        where: { userId: actorId, organizationId: payor.organizationId },
      });
      orgShared = !!m;
    }
    if (!ownsIt && !orgShared) {
      throw new Error("You don't have access to that payor");
    }
    // Auto-sync brandName from the payor's canonical legalName so the
    // free-text column stays consistent for downstream reads.
    dealData.brandName = payor.legalName;
    if (payor.contactName && !dealData.brandContact) dealData.brandContact = payor.contactName;
    if (payor.contactEmail && !dealData.brandEmail) dealData.brandEmail = payor.contactEmail;
  }

  // US-029: If the client sent structured compensation items, the cached
  // `amount` becomes the sum across all items. Otherwise synthesise a
  // single CASH item from the legacy `amount` so the structured view
  // shows the deal correctly for clients that haven't migrated yet.
  const itemsToCreate = (
    compensationItems?.length
      ? compensationItems
      : [
          {
            type: 'CASH' as const,
            amount: dealData.amount ?? 0,
            currency: dealData.currency ?? 'USD',
            description: 'Cash payment',
            orderIndex: 0,
          },
        ]
  ).map((it, idx) => ({
    id: createId(),
    type: it.type,
    amount: it.amount,
    currency: it.currency ?? dealData.currency ?? 'USD',
    description: it.description,
    unitLabel: 'unitLabel' in it && it.unitLabel ? it.unitLabel : null,
    royaltyPercent:
      'royaltyPercent' in it && it.royaltyPercent !== undefined ? it.royaltyPercent : null,
    expectedVolumeLow:
      'expectedVolumeLow' in it && it.expectedVolumeLow !== undefined ? it.expectedVolumeLow : null,
    expectedVolumeHigh:
      'expectedVolumeHigh' in it && it.expectedVolumeHigh !== undefined
        ? it.expectedVolumeHigh
        : null,
    dueDate: 'dueDate' in it && it.dueDate ? new Date(it.dueDate) : null,
    orderIndex: idx,
  }));

  // Keep the cached `amount` consistent with the items when items came
  // through. (For the synthesised-CASH path, dealData.amount already
  // matches by construction.)
  const computedAmount = compensationItems?.length
    ? compensationItems.reduce((s, it) => s + Number(it.amount || 0), 0)
    : dealData.amount;

  const dealId = createId();
  const deal = await db.deal.create({
    data: {
      id: dealId,
      ...dealData,
      amount: computedAmount,
      startDate: dealData.startDate ? new Date(dealData.startDate) : undefined,
      endDate: dealData.endDate ? new Date(dealData.endDate) : undefined,
      athleteId,
      createdByAgentId,
      commissionPercent,
      deliverables: deliverables?.length
        ? {
            // US-030: legacy callers send freeform `type` only; map it
            // server-side to a NIL Go activity category so the
            // /deals/$dealId UI doesn't render every legacy-created
            // deliverable as "needs detail".
            create: deliverables.map((d) => ({
              id: createId(),
              title: d.title,
              type: d.type,
              platform: d.platform,
              activityCategory: deriveActivityCategoryFromLegacyType(d.type),
              dueDate: d.dueDate ? new Date(d.dueDate) : undefined,
            })),
          }
        : undefined,
      compensationItems: { create: itemsToCreate },
    },
  });

  await captureServerEvent({
    // Attribute the event to whoever acted, so agent activity is
    // distinguishable from athlete self-serve in analytics.
    distinctId: actorId,
    event: 'deal_created',
    properties: {
      dealId: deal.id,
      brandName: dealData.brandName,
      organizationId: dealData.organizationId ?? null,
      amount: dealData.amount,
      currency: dealData.currency,
      status: dealData.status,
      deliverableCount: deliverables?.length ?? 0,
      onBehalf: createdByAgentId !== null,
    },
  });

  return { id: deal.id };
}

/**
 * Resolve the commission rate that applies to a deal.
 *
 * Per-deal override wins; otherwise the agency membership's default
 * rate; otherwise none. Kept pure and exported so the rollups in
 * `agency.listRoster` / `listDeals` and the tests agree on one rule.
 */
export function resolveCommissionPercent(
  dealPercent: number | null | undefined,
  memberPercent: number | null | undefined,
): number | null {
  if (dealPercent !== null && dealPercent !== undefined) return Number(dealPercent);
  if (memberPercent !== null && memberPercent !== undefined) return Number(memberPercent);
  return null;
}

/** Commission earned on a deal amount, or 0 when no rate applies. */
export function commissionEarned(
  amount: number,
  dealPercent: number | null | undefined,
  memberPercent: number | null | undefined,
): number {
  const pct = resolveCommissionPercent(dealPercent, memberPercent);
  if (pct === null) return 0;
  return (Number(amount) || 0) * (pct / 100);
}

/**
 * The date that decides "when was this deal struck".
 *
 * `signedDate` is the truth when present — that's the moment the deal
 * became real. `startDate` is the next best proxy (the term began), and
 * `createdAt` is the last resort. Deliberately NOT `createdAt`-first: a
 * backfilled deal entered into NilBoard today may have been signed years
 * ago, and crediting an agent for it would be wrong.
 */
export function effectiveDealDate(deal: {
  signedDate?: Date | string | null;
  startDate?: Date | string | null;
  createdAt?: Date | string | null;
}): Date | null {
  const pick = deal.signedDate ?? deal.startDate ?? deal.createdAt ?? null;
  if (!pick) return null;
  const d = pick instanceof Date ? pick : new Date(pick);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Does this deal fall inside the agency's representation window?
 *
 * `contractStartDate === null` means COUNT EVERYTHING — most memberships
 * have no contract concept, and an agency that hasn't set a date yet must
 * not suddenly see zeroes. A deal struck exactly ON the start date counts
 * (the boundary is inclusive); that's the day representation began.
 */
export function isWithinContractWindow(
  deal: { signedDate?: Date | string | null; startDate?: Date | string | null; createdAt?: Date | string | null },
  contractStartDate: Date | string | null | undefined,
): boolean {
  if (contractStartDate === null || contractStartDate === undefined) return true;
  const start =
    contractStartDate instanceof Date ? contractStartDate : new Date(contractStartDate);
  if (Number.isNaN(start.getTime())) return true;
  const dealDate = effectiveDealDate(deal);
  // A deal with no usable date at all can't be proven out-of-window, so
  // count it rather than silently hiding value from the agent.
  if (!dealDate) return true;
  return dealDate.getTime() >= start.getTime();
}

/**
 * Is this deal an agent-created draft still awaiting the athlete's
 * confirmation? (D9) Exported so the API, the deal page banner, and the
 * tests share one definition instead of re-deriving the predicate.
 */
export function isAwaitingAthleteConfirmation(deal: {
  status: string;
  createdByAgentId?: string | null;
}): boolean {
  return deal.status === 'DRAFT' && Boolean(deal.createdByAgentId);
}
